import React from 'react';
import moment from 'moment';
import { AiConversationsButton } from '../Common/DataTable/tableActionButtons';
import { hideSensitiveData } from '../Common/DataTable/tableHelpers';
import { CONVERSATION_STATUS } from '../../config/aiConversationStatus';
import { aiRoutingModes } from '../../config/aiAgents';
import TimeLeftBadge from './TimeLeftBadge';
import HelpBadge from './HelpBadge';

// What each live state means for whoever is watching the floor.
const STATUS_HELP = {
  starting: 'The agent is sending the first WhatsApp message',
  waiting: 'The first message went out and she has not answered yet',
  active: 'She answered and the agent is talking with her',
};

// Sorting by time left puts first whoever goes back soonest: the ones
// already going back, then the ones extended while she writes, then the
// countdowns, and the unmeasurable ones last.
const timeLeftOrder = ({ state, ms }) => {
  if (state === 'goingBack') return -2;
  if (state === 'extended') return -1;
  if (state === 'running') return ms;
  return Number.MAX_SAFE_INTEGER;
};

const when = (date) => (date ? moment(date).format('DD/MM HH:mm') : '-');

const createTableColumns = ({ openConversation, showSensitiveData, t }) => [
  {
    title: 'ID',
    name: 'id',
    hidden: true,
  },
  {
    title: 'Name',
    name: 'leadName',
    formatterComponent: ({ value }) =>
      showSensitiveData ? value : hideSensitiveData(value, 4),
    filter: showSensitiveData ? undefined : 'disabled',
    sorting: showSensitiveData ? undefined : 'disabled',
  },
  {
    title: 'Phone',
    name: 'participantPhone',
    formatterComponent: ({ value }) =>
      showSensitiveData ? value : hideSensitiveData(value, 6),
    filter: showSensitiveData ? undefined : 'disabled',
    sorting: showSensitiveData ? undefined : 'disabled',
  },
  {
    title: 'Acquisition Order',
    name: 'acquisitionOrderName',
  },
  {
    title: 'AI Agent',
    name: 'agentName',
    getCellValue: (row) => (row.agentConfig || {}).name || row.agentId || '',
  },
  {
    title: 'Mode',
    name: 'mode',
    width: 110,
    getCellValue: (row) => {
      const mode = aiRoutingModes.find(({ value }) => value === row.mode);
      return mode ? t(mode.label) : row.mode;
    },
  },
  {
    title: 'Status',
    name: 'status',
    width: 160,
    getCellValue: (row) => {
      const status = CONVERSATION_STATUS[row.status];
      return status ? t(status.title) : row.status;
    },
    formatterComponent: ({ value, row }) => (
      <HelpBadge
        id={`status_${row.id}`}
        color={(CONVERSATION_STATUS[row.status] || {}).color || 'light'}
        help={STATUS_HELP[row.status] && t(STATUS_HELP[row.status])}
      >
        {value}
      </HelpBadge>
    ),
  },
  {
    title: 'Started',
    name: 'startedAt',
    width: 110,
    formatterComponent: ({ value }) => when(value),
    filter: 'disabled',
  },
  {
    title: 'Last reply',
    name: 'lastLeadMessageAt',
    width: 110,
    formatterComponent: ({ value }) => when(value),
    filter: 'disabled',
  },
  {
    title: 'Turns',
    name: 'turnCount',
    width: 80,
    formatterComponent: ({ row }) =>
      `${row.turnCount || 0} / ${(row.agentConfig || {}).maxTurns || '-'}`,
    filter: 'disabled',
  },
  {
    title: 'Time left',
    name: 'timeLeft',
    width: 200,
    getCellValue: (row) => timeLeftOrder(row.timeLeft),
    formatterComponent: ({ row }) => (
      <TimeLeftBadge id={row.id} value={row.timeLeft} />
    ),
    filter: 'disabled',
  },
  // Last, as in every other table of the CRM.
  {
    title: 'Action',
    name: 'action',
    width: 90,
    align: 'center',
    isDummyField: true,
    formatterComponent: ({ row }) => (
      <ul className="list-inline font-size-20 contact-links mb-0">
        <AiConversationsButton row={row} onPress={openConversation} />
      </ul>
    ),
    filter: 'disabled',
    sorting: 'disabled',
  },
];

export default createTableColumns;
